import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {
  ComplexPolymerConnectionType,
  connectionHash,
  ResolvedAttachmentGraphQL,
  ResolvedAttachmentInput,
  resolvedAttachmentHash,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BioentityChip} from 'ui-library/chips/BioentityChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {
  getConnectionInputs,
  getExistingResolvedAttachmentInputs,
  getResolvedAttachmentInput,
} from '../ComplexPolymerUtil';
import {ComplexPolymerUpdateData, updateComplexPolymer} from './ComplexPolymerComponentsSummary';

interface ComplexPolymerRemoveAttachmentWarningModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => Promise<void>;
  resolvedAttachment: ResolvedAttachmentGraphQL;
  complexPolymerData: ComplexPolymerUpdateData;
}

function ComplexPolymerRemoveAttachmentWarningModal(
  props: ComplexPolymerRemoveAttachmentWarningModalProps
): JSX.Element {
  const buttons = [
    {
      text: 'Cancel',
      disabled: false,
      submit: false,
      onClick: props.onRequestClose,
      style: ButtonStyle.TERTIARY_GRAY,
    },
    {
      text: 'Detach',
      disabled: false,
      submit: true,
      style: ButtonStyle.NEGATIVE,
    },
  ];
  return (
    <Modal
      title="Detach conjugate"
      mediumLarge={true}
      buttons={buttons}
      onRequestClose={props.onRequestClose}
      scrollable={false}
      onRequestSubmit={props.onRequestSubmit}
    >
      <div className={css(styles.bodyContainer)}>
        <span className={css(styles.warningText)}>
          Detaching this conjugate will remove all connections between components.
        </span>
        <span className={css(styles.warningText)}>Do you want to proceed?</span>
        <div className={css(styles.detachPreview)}>
          <ResolvedAttachmentSummary {...props} enableEdits={false} />
        </div>
      </div>
    </Modal>
  );
}
interface RemoveResolvedAttachmentButtonProps {
  resolvedAttachment: ResolvedAttachmentGraphQL;
  complexPolymerData: ComplexPolymerUpdateData;
  onRemoveAttachment: () => Promise<void>;
  resolvedAttachments: Array<ResolvedAttachmentInput>;
}

function RemoveResolvedAttachmentButton({
  resolvedAttachment,
  complexPolymerData,
  onRemoveAttachment,
}: RemoveResolvedAttachmentButtonProps): JSX.Element {
  return (
    <div className={css(styles.attachmentIcons)}>
      <Button
        size={ButtonSize.MINI}
        buttonStyle={ButtonStyle.NEGATIVE}
        icon="unlink"
        onClick={() =>
          runModal<ComplexPolymerRemoveAttachmentWarningModalProps>(
            ComplexPolymerRemoveAttachmentWarningModal,
            {
              onRequestSubmit: onRemoveAttachment,
              resolvedAttachment,
              complexPolymerData,
            }
          )
        }
      />
    </div>
  );
}

export interface ResolvedAttachmentSummaryProps {
  complexPolymerData: ComplexPolymerUpdateData;
  resolvedAttachment: ResolvedAttachmentGraphQL;
  enableEdits: boolean;
}

export default function ResolvedAttachmentSummary({
  complexPolymerData,
  resolvedAttachment,
  enableEdits,
}: ResolvedAttachmentSummaryProps): JSX.Element | null {
  const [hoverAttachmentSummary, setHoverAttachmentSummary] = useState(false);
  const resolvedAttachmentInput = getResolvedAttachmentInput(resolvedAttachment);
  const allResolvedAttachmentInputs = getExistingResolvedAttachmentInputs(
    complexPolymerData.resolvedAttachments
  );
  const resolvedAttachmentConnectionHashes = new Set(
    resolvedAttachmentInput.connections_involved.map((connection) => connectionHash(connection))
  );

  const onRemoveAttachment = async (): Promise<void> => {
    await withLoadingScreen(
      updateComplexPolymer(
        complexPolymerData.complexPolymerType,
        complexPolymerData.complexPolymerId,
        complexPolymerData.versionId,
        complexPolymerData.simplePolymers,
        // Hydrogen bond connections will be automatically reinstated by the backend.
        getConnectionInputs(complexPolymerData.connections).filter(
          (connection) =>
            connection.connection_type === ComplexPolymerConnectionType.COVALENT_BOND &&
            !resolvedAttachmentConnectionHashes.has(connectionHash(connection))
        ),
        allResolvedAttachmentInputs.filter(
          (attachment: ResolvedAttachmentInput) =>
            !(resolvedAttachmentHash(attachment) === resolvedAttachmentHash(resolvedAttachmentInput))
        ),
        complexPolymerData.offset ?? null
      )
    );
  };

  if (resolvedAttachment.chemicalStructureImage == null) {
    return null;
  }
  return (
    <div className={css(styles.resolvedAttachmentWrapper)}>
      <div className={css(styles.resolvedAttachmentSummary)}>
        <div
          onMouseEnter={() => {
            setHoverAttachmentSummary(true);
          }}
          onMouseLeave={() => {
            setHoverAttachmentSummary(false);
          }}
          className={
            enableEdits
              ? css(styles.conjugateSummary, styles.conjugateSummaryHover)
              : css(styles.conjugateSummary)
          }
          {...getTestProps({
            component: 'ResolvedAttachmentSummary',
            element: 'conjugateSummary',
            key: resolvedAttachment.displayName,
          })}
        >
          <div className={css(styles.displayNameChip)}>
            <Chip text={resolvedAttachment.displayName} size={ChipSize.MINI} />
          </div>
          <div>
            {resolvedAttachment.chemicalStructureImage.__typename !== 'ErrorNode' &&
              resolvedAttachment.chemicalStructureImage.base64Svg != null && (
                <img
                  className={css(styles.resolvedAttachmentImage)}
                  src={`data:image/svg+xml;base64,${resolvedAttachment.chemicalStructureImage.base64Svg}`}
                />
              )}
            {resolvedAttachment.chemicalStructureImage.__typename !== 'SVGNode' &&
              resolvedAttachment.chemicalStructureImage.message != null && (
                <div>{resolvedAttachment.chemicalStructureImage.message}</div>
              )}
            {enableEdits && hoverAttachmentSummary && (
              <RemoveResolvedAttachmentButton
                resolvedAttachment={resolvedAttachment}
                complexPolymerData={complexPolymerData}
                resolvedAttachments={allResolvedAttachmentInputs}
                onRemoveAttachment={onRemoveAttachment}
              />
            )}
          </div>
        </div>
        <div className={css(styles.resolveAttachmentComponents)}>
          {resolvedAttachment.polymers.map((polymer) => (
            <div key={polymer.id} className={css(styles.resolvedAttachmentComponentChip)}>
              <BioentityChip
                id={
                  !(
                    polymer.entity.__typename === 'MoleculeNode' ||
                    polymer.entity.__typename === 'SequenceNode' ||
                    polymer.entity.__typename === 'ProteinNode'
                  )
                    ? null
                    : polymer.entity.id
                }
                size={ChipSize.SMALL}
                validationExtras={null}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  displayNameChip: {
    display: 'inline-block',
    pointerEvents: 'none',
  },
  conjugateSummary: {
    display: 'inline-block',
    padding: '4px',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    margin: '4px',
    background: Color.WHITE,
    position: 'relative',
  },
  conjugateSummaryHover: {
    ':hover': {
      background: Color.GRAY_200,
    },
  },
  resolvedAttachmentSummary: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: '8px',
    background: Color.GRAY_50,
    width: 'fit-content',
    marginBottom: '20px',
  },
  resolveAttachmentComponents: {
    display: 'flex',
    flexDirection: 'column',
    width: 'max-content',
  },
  resolvedAttachmentComponentChip: {
    padding: '5px',
  },
  resolvedAttachmentWrapper: {
    paddingRight: '20px',
    width: 'fit-content',
  },
  resolvedAttachmentImage: {
    width: 'auto',
    zIndex: 1,
    maxWidth: '500px',
    maxHeight: '200px',
  },
  attachmentIcons: {
    position: 'absolute',
    top: '41%',
    left: '45%',
    zIndex: 2,
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  detachPreview: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: '10px',
  },
  warningText: {
    fontSize: '14px',
  },
});
